import { FirebaseAnalytics } from '@capacitor-firebase/analytics';
import { Device } from '@capacitor/device';
import { Capacitor } from '@capacitor/core';
import { App } from '@capacitor/app';
import { useUserStore } from '@/modules/profile/stores/user.ts';
import { i18n } from '@/core/plugins';
import {
  AnalyticsEventName,
  AppSessionSourceNames,
  AppSessionDeliveryChannelNames,
} from './types';
import type { AnalyticsEvent } from './types';

// ============================================================================
// Private Module State
// ============================================================================

let lastSetUserId: string | null = null;
let isInitialized = false;

let hasActiveSession = false;
let sessionSource: AppSessionSourceNames = AppSessionSourceNames.Organic;
let deliveryChannel: AppSessionDeliveryChannelNames | undefined = undefined;
let lastActiveAt: number = Date.now();
let isAppActive = true;
let sessionStartTimer: ReturnType<typeof setTimeout> | null = null;

const SESSION_TIMEOUT_MS = 1800 * 1000; // 30 minutes

// ============================================================================
// Logging Helpers
// ============================================================================

const logDebug = (...args: unknown[]) => {
  console.log(
    '%cAnalytics',
    'background:#e67e22;color:white;padding:3px 6px;border-radius:3px;font-weight:bold',
    ...args,
  );
};

const logError = (...args: unknown[]) => {
  console.error(
    '%cAnalytics',
    'background:#c0392b;color:white;padding:3px 6px;border-radius:3px;font-weight:bold',
    ...args,
  );
};

// ============================================================================
// Private Helpers
// ============================================================================

const getUserId = (): string | null => {
  return useUserStore().user?.id ?? null;
};

const buildSessionParams = () => ({
  sessionSource: sessionSource,
  ...(deliveryChannel ? {
    deliveryChannel: deliveryChannel,
  } : {
  }),
});

const setStaticKeysToReport = async (): Promise<void> => {
  if (!Capacitor.isNativePlatform()) {
    return;
  }

  try {
    await FirebaseAnalytics.setUserProperty({
      key: 'language',
      value: i18n.global.locale || 'en',
    });

    const deviceInfo = await Device.getInfo();

    await FirebaseAnalytics.setUserProperty({
      key: 'webViewVersion',
      value: deviceInfo.webViewVersion || 'unknown',
    });

    await FirebaseAnalytics.setUserProperty({
      key: 'isOnEmulator',
      value: JSON.stringify(deviceInfo.isVirtual),
    });

    await FirebaseAnalytics.setUserProperty({
      key: 'androidSDKVersion',
      value: String(deviceInfo.androidSDKVersion || 'unknown'),
    });

    await FirebaseAnalytics.setUserProperty({
      key: 'iOSVersion',
      value: String(deviceInfo.iOSVersion || 'unknown'),
    });

    await FirebaseAnalytics.setUserProperty({
      key: 'osVersion',
      value: deviceInfo.osVersion,
    });
  } catch (error) {
    logError('Failed to set static user properties:', error);
  }
};

const ensureUserSet = async (): Promise<void> => {
  if (!Capacitor.isNativePlatform()) {
    return;
  }

  try {
    const currentUserId = getUserId();

    if (lastSetUserId !== currentUserId) {
      await FirebaseAnalytics.setUserId({
        userId: currentUserId,
      });
      lastSetUserId = currentUserId;
    }
  } catch (error) {
    logError('Failed to set user ID:', error);
  }
};

const parseUtmSource = (urlStr: string): AppSessionDeliveryChannelNames => {
  try {
    const url = new URL(urlStr);
    const utmSource = url.searchParams.get('utm_source')?.toLowerCase();

    if (!utmSource) {
      return AppSessionDeliveryChannelNames.Unknown;
    }

    const allowedValues = Object.values(AppSessionDeliveryChannelNames) as string[];
    if (allowedValues.includes(utmSource)) {
      return utmSource as AppSessionDeliveryChannelNames;
    }

    return AppSessionDeliveryChannelNames.Unknown;
  } catch {
    return AppSessionDeliveryChannelNames.Unknown;
  }
};

/**
 * Firebase Analytics param values that survive the Capacitor bridge with correct types
 * (see FirebaseAnalyticsHelper.createBundleFromJson: String | Boolean | Number).
 */
type FirebaseAnalyticsParamValue = string | number | boolean;

/**
 * Convert typed AnalyticsEvent to Firebase-compatible params.
 * Preserves string / number / boolean so GA4 can treat numeric params as metrics.
 * Null/undefined are omitted. Arrays are joined (GA4 custom defs expect scalars).
 */
const normalizeParams = (
  params: Record<string, unknown>,
): Record<string, FirebaseAnalyticsParamValue> => {
  const normalized: Record<string, FirebaseAnalyticsParamValue> = {};

  for (const [key, value] of Object.entries(params)) {
    if (value === null || value === undefined) {
      continue;
    }

    if (typeof value === 'string' || typeof value === 'boolean') {
      normalized[key] = value;
      continue;
    }

    if (typeof value === 'number' && Number.isFinite(value)) {
      normalized[key] = value;
      continue;
    }

    if (Array.isArray(value)) {
      normalized[key] = value.join(',');
      continue;
    }

    normalized[key] = String(value);
  }

  return normalized;
};

// ============================================================================
// Session Management
// ============================================================================

export const startAnalyticsSession = async (
  source: AppSessionSourceNames,
  url?: string,
): Promise<void> => {
  if (sessionStartTimer) {
    clearTimeout(sessionStartTimer);
    sessionStartTimer = null;
  }

  hasActiveSession = true;
  sessionSource = source;
  lastActiveAt = Date.now();
  deliveryChannel = source === AppSessionSourceNames.DeepLink && url
    ? parseUtmSource(url)
    : undefined;

  logDebug('Session started:', buildSessionParams());

  const logSessionStart = async () => {
    sessionStartTimer = null;
    await logAnalyticsEvent({
      name: AnalyticsEventName.AppSessionStart,
      params: {
        sessionSource: sessionSource,
        ...(deliveryChannel ? {
          deliveryChannel: deliveryChannel,
        } : {
        }),
      },
    });
  };

  if (source === AppSessionSourceNames.Organic) {
    // Delay organic session start to allow push/deeplink to override during cold start
    sessionStartTimer = setTimeout(logSessionStart, 1500);
  } else {
    await logSessionStart();
  }
};

/**
 * Check if current session is still valid, if not start a new one.
 * If a session was just started as 'organic' and we now have a better source (push/deeplink),
 * we update the source of the current session.
 */
export const refreshAnalyticsSession = async (
  source: AppSessionSourceNames = AppSessionSourceNames.Organic,
  url?: string,
): Promise<void> => {
  const now = Date.now();
  const isExpired = now - lastActiveAt > SESSION_TIMEOUT_MS;

  if (!hasActiveSession || isExpired) {
    await startAnalyticsSession(source, url);

    return;
  }

  const canUpgrade =
    sessionSource === AppSessionSourceNames.Organic &&
    source !== AppSessionSourceNames.Organic &&
    now - lastActiveAt < 10000; // 10 second window to upgrade organic to push/deeplink

  if (canUpgrade) {
    if (sessionStartTimer) {
      clearTimeout(sessionStartTimer);
      sessionStartTimer = null;
    }

    sessionSource = source;
    deliveryChannel = source === AppSessionSourceNames.DeepLink && url
      ? parseUtmSource(url)
      : undefined;

    logDebug('Session source upgraded:', buildSessionParams());

    await logAnalyticsEvent({
      name: AnalyticsEventName.AppSessionStart,
      params: {
        sessionSource: sessionSource,
        ...(deliveryChannel ? {
          deliveryChannel: deliveryChannel,
        } : {
        }),
      },
    });
  } else {
    lastActiveAt = now;
  }
};

// ============================================================================
// Public Analytics Functions
// ============================================================================

export const resetAnalyticsState = (): void => {
  lastSetUserId = null;
  hasActiveSession = false;
  sessionSource = AppSessionSourceNames.Organic;
  deliveryChannel = undefined;
  lastActiveAt = Date.now();
  isAppActive = true;

  if (sessionStartTimer) {
    clearTimeout(sessionStartTimer);
    sessionStartTimer = null;
  }
};

export const initializeAnalytics = async (): Promise<void> => {
  if (!Capacitor.isNativePlatform()) {
    return;
  }

  if (isInitialized) {
    return;
  }

  isInitialized = true;

  try {
    await FirebaseAnalytics.setSessionTimeoutDuration({
      duration: 1800,
    });

    await setStaticKeysToReport();

    await refreshAnalyticsSession();

    App.addListener('appStateChange', async ({ isActive }) => {
      try {
        isAppActive = isActive;

        if (isActive) {
          await refreshAnalyticsSession();
        }
      } catch (error) {
        logError('Failed to handle app state change:', error);
      }
    });
  } catch (error) {
    isInitialized = false;
    logError('Failed to initialize analytics:', error);
  }
};

export const logAnalyticsEvent = async (event: AnalyticsEvent): Promise<void> => {
  if (!hasActiveSession) {
    await refreshAnalyticsSession();
  }

  if (isAppActive) {
    lastActiveAt = Date.now();
  }

  const combinedParams: Record<string, unknown> = {
    ...buildSessionParams(),
    ...event.params,
  };

  if (!Capacitor.isNativePlatform()) {
    logDebug(`Event: ${event.name}`, combinedParams);

    return;
  }

  logDebug(`Event: ${event.name}`, combinedParams);

  try {
    await ensureUserSet();

    if (event.name === AnalyticsEventName.ScreenView) {
      const screenName = String(event.params.screenName || 'unknown');

      await FirebaseAnalytics.setCurrentScreen({
        screenName,
        screenClassOverride: screenName,
      });

      combinedParams.screenName = screenName;
    }

    await FirebaseAnalytics.logEvent({
      name: event.name,
      params: normalizeParams(combinedParams),
    });
  } catch (error) {
    logError('Failed to log event:', error);
  }
};
